import React, { useEffect, useState } from 'react';
import {
  HardDrive,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Clock,
  Layers,
  FileVideo,
  Search,
  Filter,
  Users,
  Database,
  ShieldCheck,
  Ban,
  Video,
  ListVideo,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import {
  DriveTransferMasterStats,
  YouTubeTransferMasterStats,
  MasterTransferBatch,
} from '../types/index.js';

export const DriveTransfers: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'google_drive' | 'youtube'>('google_drive');
  const [driveStats, setDriveStats] = useState<DriveTransferMasterStats | null>(null);
  const [ytStats, setYtStats] = useState<YouTubeTransferMasterStats | null>(null);
  const [batches, setBatches] = useState<MasterTransferBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      if (activeTab === 'youtube') {
        const [resStats, resBatches] = await Promise.allSettled([
          api.getMasterYouTubeStats(),
          api.getMasterYouTubeTransfers({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        ]);

        if (resStats.status === 'fulfilled') {
          setYtStats(resStats.value);
        }
        if (resBatches.status === 'fulfilled') {
          setBatches(resBatches.value.items || []);
        }
      } else {
        const [resStats, resBatches] = await Promise.allSettled([
          api.getMasterDriveStats(),
          api.getMasterDriveTransfers({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        ]);

        if (resStats.status === 'fulfilled') {
          setDriveStats(resStats.value);
        }
        if (resBatches.status === 'fulfilled') {
          setBatches(resBatches.value.items || []);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar monitoramento de transferências:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 5000);
    return () => clearInterval(interval);
  }, [activeTab, statusFilter]);

  const formatBytes = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getStatusBadge = (status: MasterTransferBatch['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-50 text-status-success border border-green-200">
            <CheckCircle2 size={13} /> Concluído
          </span>
        );
      case 'completed_with_errors':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={13} /> Com Erros
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-status-info border border-blue-200">
            <RefreshCw size={13} className="animate-spin text-status-info" /> Ativo
          </span>
        );
      case 'queued':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-txt-secondary border border-slate-200">
            <Clock size={13} /> Pendente
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-txt-muted border border-slate-200">
            <Ban size={13} /> Cancelado
          </span>
        );
      case 'failed':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-status-error border border-red-200">
            <XCircle size={13} /> Falha
          </span>
        );
    }
  };

  const filteredBatches = batches.filter((b) => {
    const term = searchTerm.toLowerCase();
    const matchCust = (b.customerName || b.customerId || '').toLowerCase().includes(term);
    const matchEmail = (b.googleAccountEmail || b.channelTitle || '').toLowerCase().includes(term);
    const matchProj = (b.projectNames || []).some((pn) => pn.toLowerCase().includes(term));
    return matchCust || matchEmail || matchProj;
  });

  return (
    <div>
      <Header
        title={`Transferências ${activeTab === 'youtube' ? 'YouTube' : 'Google Drive'} — Master`}
        subtitle="Monitoramento operacional global das conexões e fila de exportações dos clientes"
        action={
          <EduButton
            variant="secondary"
            size="sm"
            onClick={() => loadData()}
            icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
          >
            Atualizar
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Abas Provedor */}
        <div className="flex items-center gap-2 border-b border-bdr pb-1">
          <button
            onClick={() => {
              setActiveTab('google_drive');
              setStatusFilter('all');
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'google_drive'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            <HardDrive size={16} /> Google Drive
          </button>

          <button
            onClick={() => {
              setActiveTab('youtube');
              setStatusFilter('all');
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'youtube'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            <svg className="w-4 h-4 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            YouTube
          </button>
        </div>

        {/* Métricas Principais (Seção 23 Master) */}
        {activeTab === 'youtube' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Canais e Usuários Conectados */}
            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Canais Conectados</p>
                <p className="text-2xl font-bold text-txt-primary mt-1">
                  {ytStats?.channelsConnected ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  {ytStats?.validConnections ?? 0} válidos • {ytStats?.clientsConnected ?? 0} usuários
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-50 text-[#FF0000] border border-red-100 flex items-center justify-center">
                <Video size={20} />
              </div>
            </div>

            {/* 2. Playlists e Projetos Exportados */}
            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Playlists Criadas</p>
                <p className="text-2xl font-bold text-brand-primary mt-1">
                  {ytStats?.playlistsCreated ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  Em {ytStats?.projectsExported ?? 0} projetos exportados
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-brand-primary border border-orange-100 flex items-center justify-center">
                <ListVideo size={20} />
              </div>
            </div>

            {/* 3. Vídeos Enviados & Fila */}
            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Vídeos Enviados</p>
                <p className="text-2xl font-bold text-status-success mt-1">
                  {ytStats?.videosTransferred ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  {ytStats?.activeTransfers ?? 0} ativos • {ytStats?.pendingTransfers ?? 0} na fila
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-green-50 text-status-success border border-green-100 flex items-center justify-center">
                <CheckCircle2 size={20} />
              </div>
            </div>

            {/* 4. Erros e Quotas */}
            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Erros & Quotas</p>
                <p className="text-2xl font-bold text-status-error mt-1">
                  {ytStats?.failedTransfers ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  {ytStats?.quotaErrors ?? 0} quota excedida • {ytStats?.authorizationErrors ?? 0} auth
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-50 text-status-error border border-red-100 flex items-center justify-center">
                <AlertTriangle size={20} />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Clientes Conectados</p>
                <p className="text-2xl font-bold text-txt-primary mt-1">
                  {driveStats?.clientsConnected ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  {driveStats?.validConnections ?? 0} válidas • {driveStats?.errorConnections ?? 0} com erro
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-brand-primary border border-orange-100 flex items-center justify-center">
                <HardDrive size={20} />
              </div>
            </div>

            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Vídeos Enviados</p>
                <p className="text-2xl font-bold text-status-success mt-1">
                  {driveStats?.videosTransferred ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  Em {driveStats?.projectsTransferred ?? 0} projetos distintos
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-green-50 text-status-success border border-green-100 flex items-center justify-center">
                <CheckCircle2 size={20} />
              </div>
            </div>

            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Transferências Ativas</p>
                <p className="text-2xl font-bold text-status-info mt-1">
                  {driveStats?.activeTransfers ?? 0}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  {driveStats?.pendingTransfers ?? 0} na fila de espera
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-status-info border border-blue-100 flex items-center justify-center">
                <RefreshCw size={20} className={driveStats?.activeTransfers ? 'animate-spin' : ''} />
              </div>
            </div>

            <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs text-txt-secondary font-medium">Volume no Mês</p>
                <p className="text-2xl font-bold text-txt-primary mt-1 font-mono">
                  {formatBytes(driveStats?.monthlyBytesTransferred ?? 0)}
                </p>
                <p className="text-[11px] text-txt-muted mt-0.5">
                  <span className={driveStats?.failedTransfers ? 'text-status-error font-semibold' : ''}>
                    {driveStats?.failedTransfers ?? 0} falhas operacionais
                  </span>
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-txt-secondary border border-bdr flex items-center justify-center">
                <Database size={20} />
              </div>
            </div>
          </div>
        )}

        {/* Barra de Filtros e Busca */}
        <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 text-txt-muted" size={16} />
            <input
              type="text"
              placeholder="Buscar por cliente, canal ou projeto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-surface-secondary border border-bdr rounded-lg text-txt-primary placeholder:text-txt-muted focus:outline-none focus:border-brand-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-txt-secondary font-medium">Filtrar por Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-surface border border-bdr rounded-lg px-3 py-1.5 text-xs text-txt-primary focus:outline-none focus:border-brand-primary"
            >
              <option value="all">Todos os Status</option>
              <option value="in_progress">Em Andamento</option>
              <option value="queued">Na Fila</option>
              <option value="completed">Concluídos</option>
              <option value="failed">Com Falha</option>
              <option value="completed_with_errors">Com Erros Parciais</option>
            </select>
          </div>
        </div>

        {/* Tabela de Lotes Master */}
        {loading && batches.length === 0 ? (
          <div className="p-16 text-center text-xs text-txt-muted flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <span>Carregando transferências globais...</span>
          </div>
        ) : filteredBatches.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <FileVideo size={36} className="mx-auto text-txt-muted/60 mb-2" />
              <p className="text-sm font-semibold text-txt-primary">Nenhum lote de transferência encontrado</p>
              <p className="text-xs text-txt-secondary mt-1">Ajuste os filtros ou aguarde novas solicitações de exportação.</p>
            </div>
          </EduCard>
        ) : (
          <div className="bg-surface border border-bdr rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-surface-secondary border-b border-bdr text-txt-secondary font-semibold">
                    <th className="py-3.5 px-4">Lote / Data</th>
                    <th className="py-3.5 px-4">Cliente / Instituição</th>
                    <th className="py-3.5 px-4">{activeTab === 'youtube' ? 'Canal YouTube' : 'Conta Google Drive'}</th>
                    <th className="py-3.5 px-4">Projetos</th>
                    <th className="py-3.5 px-4">Arquivos</th>
                    <th className="py-3.5 px-4">Volume</th>
                    <th className="py-3.5 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bdr/60">
                  {filteredBatches.map((b) => (
                    <tr key={b.id} className="hover:bg-surface-secondary/40 transition-colors">
                      <td className="py-3.5 px-4">
                        <span className="font-mono font-bold text-txt-primary block">
                          #{b.id.slice(0, 16)}
                        </span>
                        <span className="text-[11px] text-txt-muted font-mono">
                          {new Date(b.createdAt).toLocaleString('pt-BR')}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 font-semibold text-txt-primary">
                        {b.customerName || b.customerId}
                      </td>

                      <td className="py-3.5 px-4 text-txt-secondary truncate max-w-[200px]">
                        {activeTab === 'youtube'
                          ? b.channelTitle || b.channelId || 'Canal YouTube'
                          : b.googleAccountEmail || 'Drive'}
                      </td>

                      <td className="py-3.5 px-4 font-medium text-txt-primary truncate max-w-[200px]">
                        {b.projectNames?.join(', ') || `${b.projectIds?.length || 1} projeto(s)`}
                      </td>

                      <td className="py-3.5 px-4 font-mono">
                        <span className="text-status-success font-bold">{b.completedFiles || 0}</span>
                        {b.failedFiles > 0 && <span className="text-status-error font-bold"> / {b.failedFiles} err</span>}
                        <span className="text-txt-muted"> / {b.totalFiles}</span>
                      </td>

                      <td className="py-3.5 px-4 font-mono text-txt-secondary">
                        {formatBytes(b.transferredBytes || 0)}
                      </td>

                      <td className="py-3.5 px-4">
                        {getStatusBadge(b.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
